import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Radio,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router-dom";
import axios from "../../api/axios";
import { errorMessageOf, useFurgonetkaAccount, useShipments } from "../../api/shipping";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import { CountrySelect } from "../ui/countrySelect/countrySelect.component";
import { CLIENTS_ENDPOINT, ERROR_SNACKBAR, PARTS_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { TENANT_ROUTES } from "../../workspaces/tenantConfig/tenantConfig.routes";
import { DECIMAL_INPUT_REGEX, formatMoney, formatQuantity, toApiDecimal } from "../../utils";
import {
  FurgonetkaAccount,
  FurgonetkaFieldError,
  Shipment,
  ShipmentOffer,
  ShipmentParcel,
  ShipmentReceiver,
} from "../../types/shipping";

type ItemRow = { part: OptionRecord | null; quantity: string };

const EMPTY_RECEIVER: ShipmentReceiver = {
  name: "",
  company: "",
  street: "",
  postcode: "",
  city: "",
  country_code: "PL",
  phone: "",
  email: "",
};

const RECEIVER_REQUIRED: (keyof ShipmentReceiver)[] = ["name", "street", "postcode", "city", "phone", "email"];
const PARCEL_REQUIRED: (keyof ShipmentParcel)[] = ["width", "depth", "height", "weight"];

// Which step a field error belongs to, so a refusal sends the user back to it.
const stepOfPath = (path: string): number => {
  if (path.startsWith("items") || path.startsWith("receiver") || path.startsWith("client")) return 0;
  if (path.startsWith("parcels") || path.startsWith("package_type")) return 1;
  return 2;
};

/** Flatten an API error into "receiver.phone" -> message pairs: DRF's nested
 * validation errors and Furgonetka's own `errors[].path` alike. */
const collectFieldErrors = (errorData: any): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!errorData || typeof errorData !== "object") return result;

  (errorData.errors as FurgonetkaFieldError[] | undefined)?.forEach((error) => {
    // Furgonetka sometimes names the package itself as the root.
    const path = (error.path || "").replace(/^package\./, "");
    if (path) result[path] = error.message;
  });

  const walk = (value: any, path: string) => {
    if (Array.isArray(value)) {
      if (value.length && value.every((entry) => entry && typeof entry === "object" && "message" in entry)) {
        result[path] = value[0].message;
        return;
      }
      value.forEach((entry, index) => walk(entry, path ? `${path}.${index}` : String(index)));
      return;
    }
    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, entry]) => walk(entry, path ? `${path}.${key}` : key));
    }
  };
  Object.entries(errorData).forEach(([key, value]) => {
    if (!["message", "code", "status_code", "errors", "detail"].includes(key)) walk(value, key);
  });

  return result;
};

const formatDeliveryTime = (minutes: number | null, t: (key: string, options?: any) => string) => {
  if (!minutes) return "";
  const hours = Math.round(minutes / 60);
  return hours < 48 ? t("about {{hours}} h", { hours }) : t("about {{days}} days", { days: Math.round(hours / 24) });
};

interface CreateShipmentFormProps {
  /** Pre-selects this part, when opened from a part's page. */
  initialPart?: { id: number | string; name: string; stock_quantity?: string; unit?: string } | null;
  onSuccess: (shipment: Shipment) => void;
}

export default function CreateShipmentForm({ initialPart, onSuccess }: CreateShipmentFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canChangeSettings = useHasPermission("config.change_tenantsettings");
  const { getStatus } = useFurgonetkaAccount();
  const { quote, createShipment, loading } = useShipments();

  const [account, setAccount] = useState<FurgonetkaAccount | null>(null);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [step, setStep] = useState(0);
  const [items, setItems] = useState<ItemRow[]>([
    { part: initialPart ? ({ ...initialPart } as OptionRecord) : null, quantity: "1" },
  ]);
  const [client, setClient] = useState<OptionRecord | null>(null);
  const [receiver, setReceiver] = useState<ShipmentReceiver>(EMPTY_RECEIVER);
  const [parcels, setParcels] = useState<ShipmentParcel[]>([]);
  const [offers, setOffers] = useState<ShipmentOffer[] | null>(null);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      const { data, success } = await getStatus();
      if (success) {
        setAccount(data);
        setParcels([
          {
            width: String(data.default_width ?? ""),
            depth: String(data.default_depth ?? ""),
            height: String(data.default_height ?? ""),
            weight: String(data.default_weight ?? ""),
            value: "",
            description: "",
          },
        ]);
      }
      setAccountLoaded(true);
    };
    load();
  }, [getStatus]);

  const steps = [t("Parts and receiver"), t("Parcel"), t("Carrier")];

  const setReceiverField = (field: keyof ShipmentReceiver, value: string) =>
    setReceiver((previous) => ({ ...previous, [field]: value }));

  const handleClientChange = async (option: OptionRecord | null) => {
    setClient(option);
    if (!option) return;

    // The list carries no phone or e-mail - the client's own record does.
    try {
      const response = await axios.get(`${CLIENTS_ENDPOINT}${option.id}/`);
      const address = response.data?.address ?? {};
      setReceiver((previous) => ({
        ...previous,
        company: response.data?.name ?? previous.company,
        street: address.street ?? "",
        postcode: address.zip_code ?? "",
        city: address.city ?? "",
        country_code: address.country || "PL",
        phone: address.phone_number ?? "",
        email: address.email ?? "",
      }));
    } catch {
      enqueueSnackbar(t("Could not load the client's address."), ERROR_SNACKBAR);
    }
  };

  const contentsDescription = useMemo(
    () =>
      items
        .filter((item) => item.part)
        .map((item) => item.part?.name)
        .join(", ")
        .slice(0, 100),
    [items]
  );

  const validateStep = (current: number): Record<string, string> => {
    const errors: Record<string, string> = {};
    const required = t("This field is required.");
    if (current === 0) {
      items.forEach((item, index) => {
        if (!item.part) errors[`items.${index}.part`] = required;
        if (!item.quantity || Number(toApiDecimal(item.quantity)) <= 0) {
          errors[`items.${index}.quantity`] = t("Enter a quantity greater than zero.");
        } else if (
          item.part?.stock_quantity !== undefined &&
          Number(toApiDecimal(item.quantity)) > Number(item.part.stock_quantity)
        ) {
          errors[`items.${index}.quantity`] = t("Only {{quantity}} in stock.", {
            quantity: formatQuantity(item.part.stock_quantity),
          });
        }
      });
      RECEIVER_REQUIRED.forEach((field) => {
        if (!receiver[field]?.trim()) errors[`receiver.${field}`] = required;
      });
    }
    if (current === 1) {
      parcels.forEach((parcel, index) => {
        PARCEL_REQUIRED.forEach((field) => {
          if (!parcel[field] || Number(toApiDecimal(String(parcel[field]))) <= 0) {
            errors[`parcels.${index}.${field}`] = required;
          }
        });
      });
    }
    if (current === 2 && !serviceId) {
      errors.service_id = t("Choose a carrier.");
    }
    return errors;
  };

  const apiParcels = (): ShipmentParcel[] =>
    parcels.map((parcel) => ({
      width: toApiDecimal(parcel.width),
      depth: toApiDecimal(parcel.depth),
      height: toApiDecimal(parcel.height),
      weight: toApiDecimal(parcel.weight),
      ...(parcel.value ? { value: toApiDecimal(parcel.value) } : {}),
      description: parcel.description || contentsDescription,
    }));

  const showApiError = (errorData: any, fallback: string) => {
    const errors = collectFieldErrors(errorData);
    setFieldErrors(errors);
    const firstPath = Object.keys(errors)[0];
    if (firstPath !== undefined) {
      setStep(Math.min(step, stepOfPath(firstPath)));
    }
    setFormError(errorMessageOf(errorData, fallback));
  };

  const loadOffers = useCallback(async () => {
    setOffers(null);
    setServiceId(null);
    setFormError("");
    const { data, success, errorData } = await quote(receiver, apiParcels());
    if (success) {
      setOffers(data);
      const cheapest = (data as ShipmentOffer[]).find((offer) => offer.available);
      setServiceId(cheapest ? cheapest.service_id : null);
    } else {
      setOffers([]);
      showApiError(errorData, t("Could not get prices from Furgonetka."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, receiver, parcels, contentsDescription]);

  const handleNext = () => {
    const errors = validateStep(step);
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      enqueueSnackbar(t("Fill in the required fields."), ERROR_SNACKBAR);
      return;
    }
    setFormError("");
    const next = step + 1;
    setStep(next);
    if (next === 2) loadOffers();
  };

  const handleSubmit = async () => {
    const errors = validateStep(2);
    setFieldErrors(errors);
    if (Object.keys(errors).length || !serviceId) return;

    const { data, success, errorData } = await createShipment({
      items: items.map((item) => ({ part: item.part!.id, quantity: toApiDecimal(item.quantity) })),
      receiver,
      parcels: apiParcels(),
      service_id: serviceId,
      client: client?.id ?? null,
      notes,
    });

    if (success) {
      enqueueSnackbar(t("Shipment ordered. Furgonetka is confirming it."), SUCCESS_SNACKBAR);
      onSuccess(data);
    } else {
      showApiError(errorData, t("Could not order the shipment."));
    }
  };

  if (!accountLoaded) {
    return <CustomCircularProgress />;
  }

  if (!account?.available || !account.connected || !account.sender_complete) {
    return (
      <Alert severity="info">
        {!account?.available
          ? t("Courier shipments are not available in this workspace.")
          : !account.connected
          ? t("Connect your Furgonetka account to send parts by courier.")
          : t("Complete the sender details before sending the first shipment.")}
        {account?.available && canChangeSettings && (
          <Box sx={{ mt: 1 }}>
            <Button component={RouterLink} to={TENANT_ROUTES.integrations} size="small" variant="outlined">
              {t("Go to integrations")}
            </Button>
          </Box>
        )}
      </Alert>
    );
  }

  const errorOf = (path: string) => fieldErrors[path] || "";

  const receiverField = (field: keyof ShipmentReceiver, label: string, props: Record<string, any> = {}) => (
    <TextField
      size="small"
      label={label}
      value={receiver[field]}
      onChange={(event) => setReceiverField(field, event.target.value)}
      required={RECEIVER_REQUIRED.includes(field)}
      error={Boolean(errorOf(`receiver.${field}`))}
      helperText={errorOf(`receiver.${field}`)}
      fullWidth
      {...props}
    />
  );

  const renderPartsAndReceiver = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">{t("Parts")}</Typography>
      {items.map((item, index) => (
        <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Box sx={{ flex: 3 }}>
            <CustomAutocomplete
              size="small"
              apiConfig={{ url: PARTS_ENDPOINT, queryParams: { in_stock: "true", is_active: "true" } }}
              value={item.part}
              onChange={(_, value) =>
                setItems((previous) => previous.map((row, i) => (i === index ? { ...row, part: value } : row)))
              }
              getOptionLabel={(option) =>
                option.stock_quantity !== undefined
                  ? `${option.name} (${formatQuantity(option.stock_quantity)} ${option.unit ?? ""})`.trim()
                  : option.name
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Part")}
                  required
                  error={Boolean(errorOf(`items.${index}.part`))}
                  helperText={errorOf(`items.${index}.part`)}
                />
              )}
            />
          </Box>
          <TextField
            size="small"
            sx={{ flex: 1 }}
            label={t("Quantity")}
            value={item.quantity}
            onChange={(event) => {
              if (!DECIMAL_INPUT_REGEX.test(event.target.value)) return;
              setItems((previous) =>
                previous.map((row, i) => (i === index ? { ...row, quantity: event.target.value } : row))
              );
            }}
            required
            error={Boolean(errorOf(`items.${index}.quantity`))}
            helperText={errorOf(`items.${index}.quantity`)}
          />
          <IconButton
            aria-label={t("Remove")}
            disabled={items.length === 1}
            onClick={() => setItems((previous) => previous.filter((_, i) => i !== index))}
          >
            <Delete />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setItems((previous) => [...previous, { part: null, quantity: "1" }])}
        >
          {t("Add part")}
        </Button>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {t("Receiver")}
      </Typography>
      <CustomAutocomplete
        size="small"
        apiConfig={{ url: CLIENTS_ENDPOINT }}
        value={client}
        onChange={(_, value) => handleClientChange(value)}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField {...params} label={t("Client")} helperText={t("Fills in the client's address.")} />
        )}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {receiverField("company", t("Company"))}
        {receiverField("name", t("Contact person"))}
        {receiverField("street", t("Street"))}
        <Box sx={{ display: "flex", gap: 1 }}>
          {receiverField("postcode", t("Zip code"), { sx: { flex: 1 } })}
          {receiverField("city", t("City"), { sx: { flex: 2 } })}
        </Box>
        <CountrySelect
          label={t("Country")}
          value={receiver.country_code}
          onChange={(code) => setReceiverField("country_code", code)}
          error={Boolean(errorOf("receiver.country_code"))}
          helperText={errorOf("receiver.country_code")}
        />
        {receiverField("phone", t("Phone number"))}
        {receiverField("email", t("Email"), { type: "email" })}
      </Box>
    </Box>
  );

  const parcelField = (index: number, field: keyof ShipmentParcel, label: string, props: Record<string, any> = {}) => (
    <TextField
      size="small"
      label={label}
      value={parcels[index][field] ?? ""}
      onChange={(event) => {
        const value = event.target.value;
        if (field !== "description" && !DECIMAL_INPUT_REGEX.test(value)) return;
        setParcels((previous) => previous.map((parcel, i) => (i === index ? { ...parcel, [field]: value } : parcel)));
      }}
      required={PARCEL_REQUIRED.includes(field)}
      error={Boolean(errorOf(`parcels.${index}.${field}`))}
      helperText={errorOf(`parcels.${index}.${field}`)}
      fullWidth
      {...props}
    />
  );

  const renderParcels = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {parcels.map((_, index) => (
        <Box key={index} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2">
              {parcels.length > 1 ? t("Parcel {{number}}", { number: index + 1 }) : t("Parcel")}
            </Typography>
            {parcels.length > 1 && (
              <IconButton
                aria-label={t("Remove")}
                onClick={() => setParcels((previous) => previous.filter((__, i) => i !== index))}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
            {parcelField(index, "width", t("Width (cm)"))}
            {parcelField(index, "depth", t("Length (cm)"))}
            {parcelField(index, "height", t("Height (cm)"))}
            {parcelField(index, "weight", t("Weight (kg)"))}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 2fr" }, gap: 1.5 }}>
            {parcelField(index, "value", t("Declared value (PLN)"), {
              helperText: errorOf(`parcels.${index}.value`) || t("Insurance depends on it."),
            })}
            {parcelField(index, "description", t("Contents"), { placeholder: contentsDescription })}
          </Box>
        </Box>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setParcels((previous) => [...previous, { ...previous[0], value: "", description: "" }])}
        >
          {t("Add parcel")}
        </Button>
      </Box>
    </Box>
  );

  const renderOffers = () => {
    if (offers === null) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 3 }}>
          <CustomCircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t("Getting prices from the carriers...")}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {offers.length === 0 && !formError && (
          <Alert severity="warning">{t("No carrier on your Furgonetka account can take this parcel.")}</Alert>
        )}
        {offers.map((offer) => (
          <Box
            key={offer.service_id}
            onClick={() => offer.available && setServiceId(offer.service_id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              border: "1px solid",
              borderColor: serviceId === offer.service_id ? "primary.main" : "divider",
              borderRadius: 1,
              cursor: offer.available ? "pointer" : "default",
              opacity: offer.available ? 1 : 0.6,
            }}
          >
            <Radio checked={serviceId === offer.service_id} disabled={!offer.available} size="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {offer.service_name}
                </Typography>
                {offer.agreement_owner === "client" && (
                  <Chip size="small" variant="outlined" label={t("Your contract")} />
                )}
              </Box>
              {offer.available ? (
                <Typography variant="caption" color="text.secondary">
                  {formatDeliveryTime(offer.avg_delivery_time, t)}
                </Typography>
              ) : (
                <Typography variant="caption" color="error">
                  {offer.errors.map((error) => error.message).join("; ") || t("Not available for this parcel.")}
                </Typography>
              )}
            </Box>
            {offer.price_gross && (
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatMoney(offer.price_gross)} {t("gross")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatMoney(offer.price_net)} {t("net")}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
        {errorOf("service_id") && <Typography color="error" variant="caption">{errorOf("service_id")}</Typography>}
        <TextField
          size="small"
          label={t("Notes")}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={2}
          sx={{ mt: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t(
            "Ordering takes the parts out of stock straight away and charges your Furgonetka account. If the order fails or is cancelled, the parts go back to stock."
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {account.sandbox && <Alert severity="info">{t("Furgonetka test environment (sandbox) - parcels are not sent.")}</Alert>}
      <Stepper activeStep={step} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {formError && <Alert severity="error">{formError}</Alert>}

      {step === 0 && renderPartsAndReceiver()}
      {step === 1 && renderParcels()}
      {step === 2 && renderOffers()}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Button disabled={step === 0 || loading} onClick={() => setStep(step - 1)}>
          {t("Back")}
        </Button>
        {step < 2 ? (
          <Button variant="contained" onClick={handleNext}>
            {t("Next")}
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={loadOffers} disabled={loading}>
              {t("Refresh prices")}
            </Button>
            <Button variant="contained" color="success" onClick={handleSubmit} disabled={loading || !serviceId}>
              {loading ? t("Processing...") : t("Order shipment")}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
